export type RoomShortcutId =
  | "microphone"
  | "camera"
  | "screen-share"
  | "chat"
  | "raise-hand"
  | "reactions"
  | "narrator"
  | "participants"
  | "leave"
  | "invite"
  | "settings";

type RoomShortcut = {
  id: RoomShortcutId;
  key: string;
  label: string;
  requiresControl?: boolean;
};

export const ROOM_SHORTCUTS: readonly RoomShortcut[] = [
  { id: "microphone", key: "m", label: "M" },
  { id: "camera", key: "v", label: "V" },
  { id: "screen-share", key: "s", label: "S" },
  { id: "chat", key: "c", label: "C" },
  { id: "raise-hand", key: "h", label: "H" },
  { id: "reactions", key: "r", label: "R" },
  { id: "narrator", key: "n", label: "N" },
  { id: "participants", key: "p", label: "P" },
  { id: "leave", key: "l", label: "L", requiresControl: true },
  { id: "invite", key: "i", label: "I" },
  { id: "settings", key: "g", label: "G" },
];

type ShortcutDevice = Pick<
  Navigator,
  "platform" | "userAgent" | "maxTouchPoints"
>;

export function findRoomShortcut(
  event: Pick<
    KeyboardEvent,
    "key" | "altKey" | "ctrlKey" | "metaKey" | "shiftKey"
  >,
): RoomShortcutId | undefined {
  return ROOM_SHORTCUTS.find(
    (shortcut) =>
      shortcut.key === event.key.toLowerCase() &&
      (shortcut.requiresControl
        ? event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey
        : !hasShortcutModifier(event)),
  )?.id;
}

export function getRoomShortcutKeycaps(
  shortcutId: RoomShortcutId,
  isMacKeyboard: boolean,
): string[] | undefined {
  const shortcut = ROOM_SHORTCUTS.find((item) => item.id === shortcutId);
  if (!shortcut) return undefined;
  return shortcut.requiresControl
    ? [isMacKeyboard ? "⌃" : "Ctrl", shortcut.label]
    : [shortcut.label];
}

export function isEditableShortcutTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable=""], [contenteditable="true"]',
    ),
  );
}

/**
 * A panel that reads plain letters itself — a picker with type-to-search — marks
 * its surface, and the single-key shortcuts stay out of it while it is open.
 */
export function capturesShortcutKeys(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('[data-room-shortcuts-captured="true"]') !== null;
}

/** Whether this device has a keyboard whose letters the room can point at. */
export function supportsDesktopRoomShortcuts(
  device: ShortcutDevice,
  hasFineHover: boolean,
): boolean {
  const iPadDesktopMode =
    device.platform === "MacIntel" && device.maxTouchPoints > 1;
  const touchAppleDevice = /iPad|iPhone|iPod/i.test(device.userAgent);
  const mobileDevice = /Android|Mobile/i.test(device.userAgent);
  return hasFineHover && !iPadDesktopMode && !touchAppleDevice && !mobileDevice;
}

export function hasShortcutModifier(
  event: Pick<KeyboardEvent, "altKey" | "ctrlKey" | "metaKey" | "shiftKey">,
): boolean {
  return event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}
